"use client"

import { useEffect, useRef, useActionState } from "react"
import { motion } from "framer-motion"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { submitSubscribeForm, type SubscribeFormState } from "@/app/actions/subscribe"
import { useLanguage } from "@/lib/i18n"

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger)
}

export function SubscribeSection() {
  const { t, locale } = useLanguage()
  const sectionRef = useRef<HTMLElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<HTMLFormElement>(null)

  const [state, formAction, isPending] = useActionState<SubscribeFormState | null, FormData>(
    submitSubscribeForm,
    null
  )

  // GSAP entrance animation
  useEffect(() => {
    if (!sectionRef.current || !contentRef.current) return

    const ctx = gsap.context(() => {
      const items = contentRef.current?.querySelectorAll(".subscribe-item")
      if (items) {
        gsap.fromTo(
          items,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: contentRef.current,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        )
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  // Reset form on success
  useEffect(() => {
    if (state?.success) {
      formRef.current?.reset()
    }
  }, [state?.success])

  return (
    <section
      ref={sectionRef}
      id="subscribe"
      data-section="subscribe"
      className="relative py-16 md:py-20 px-6 md:px-8 lg:px-12 bg-[var(--color-bg-primary)]"
    >
      {/* Subtle top divider */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[var(--color-accent-gold)]/30 to-transparent" />

      <div ref={contentRef} className="relative z-10 max-w-[600px] mx-auto text-center">
        {/* Section label */}
        <p
          className={`subscribe-item text-2xl md:text-3xl mb-6 text-[var(--color-accent-gold)] opacity-0${locale !== "zh" ? " rotate-[-2deg]" : ""}`}
          style={{ fontFamily: "var(--font-handwritten)" }}
        >
          {t("subscribe.sectionLabel")}
        </p>

        {/* Headline */}
        <h2
          className="subscribe-item text-2xl md:text-3xl lg:text-4xl font-medium text-[var(--color-text-primary)] leading-snug mb-4 opacity-0"
          style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.01em" }}
        >
          {t("subscribe.headline")}
        </h2>

        {/* Subtitle */}
        <p className="subscribe-item text-base md:text-lg text-[var(--color-text-secondary)] leading-relaxed mb-10 opacity-0">
          {t("subscribe.subtitle")}
        </p>

        {/* Form or success state */}
        {state?.success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="subscribe-item flex flex-col items-center gap-3 py-6"
          >
            <div className="w-12 h-12 rounded-full bg-[var(--color-accent-gold)]/10 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-[var(--color-accent-gold)]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-lg text-[var(--color-text-primary)] font-medium">{state.message}</p>
          </motion.div>
        ) : (
          <form ref={formRef} action={formAction} className="subscribe-item opacity-0">
            {/* Hidden locale field */}
            <input type="hidden" name="locale" value={locale} />

            {/* Error message */}
            {state && !state.success && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 rounded-lg text-sm bg-red-500/10 text-red-400 border border-red-500/20"
              >
                {state.message}
              </motion.div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <Input
                name="name"
                type="text"
                required
                maxLength={100}
                disabled={isPending}
                placeholder={t("subscribe.namePlaceholder")}
                className="flex-1 px-4 py-3 h-12 bg-[var(--color-bg-secondary)] border border-[var(--color-bg-tertiary)] rounded-lg text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:ring-2 focus:ring-[var(--color-accent-gold)]/50 focus:border-[var(--color-accent-gold)]"
              />
              <Input
                name="email"
                type="email"
                required
                maxLength={254}
                disabled={isPending}
                placeholder={t("subscribe.emailPlaceholder")}
                className="flex-1 px-4 py-3 h-12 bg-[var(--color-bg-secondary)] border border-[var(--color-bg-tertiary)] rounded-lg text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:ring-2 focus:ring-[var(--color-accent-gold)]/50 focus:border-[var(--color-accent-gold)]"
              />
            </div>

            {/* Field errors */}
            {state?.errors?.name && (
              <p className="mb-3 text-xs text-red-400 text-left">{state.errors.name[0]}</p>
            )}
            {state?.errors?.email && (
              <p className="mb-3 text-xs text-red-400 text-left">{state.errors.email[0]}</p>
            )}

            <Button
              type="submit"
              disabled={isPending}
              className="w-full sm:w-auto px-8 py-3 h-auto rounded-full text-sm font-medium bg-[var(--color-accent-gold)] text-[var(--color-bg-primary)] hover:bg-[var(--color-accent-gold-bright)] hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
              style={{ transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)" }}
            >
              {isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t("subscribe.subscribing")}
                </span>
              ) : (
                t("subscribe.subscribe")
              )}
            </Button>

            <p className="mt-5 text-xs text-[var(--color-text-muted)]">{t("subscribe.privacy")}</p>
          </form>
        )}
      </div>

      {/* Subtle bottom divider */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[var(--color-accent-gold)]/30 to-transparent" />
    </section>
  )
}
